import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { useServerTable } from "../../../components/table/tableFeatures";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { useCategoriesQuery } from "../../../hooks/useCategories";
import { useBiddingPrograms } from "../../perf_reports/api/useBiddingPrograms";
import { useGetV4AdvReportByAd, type AdvertisementQueryV4Row } from "../../perf_reports/api/ads/v4AdvReportByAd";
import { useGetV4AdvReportOptions } from "../../perf_reports/api/ads/v4AdvReportOptions";
import { useMemo } from "react";
import { useDebounce } from "@reactuses/core";
import { MdRotateLeft } from "react-icons/md";
import { useAppSelector } from "../../../redux/hooks";
import { NTM_STATUS_OPTIONS, servingStatusOptions } from "../filters/adLifecycleFilters";
import { Table } from "../../../components/table/Table";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { RT8Pagination } from "../../../components/table/Pagination";
import { SearchInput } from "../../../components/fields/base/TextField";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { idsToVisibilityState, resetTable, selectedIdsToSelectionState } from "../../../components/table/utils";
import { AD_STATES, AD_STATUSES, GOALS } from "../../../components/core/blocks/AdPlan/constants";
import useAdReportColumns from "../../perf_reports/ad_report/useAdReportColumns";
import { useTagFilterOptions } from "../../tags/api/useTagFilterOptions";
import { useModuleEnabled } from "../../../redux/selectors/modules";

const AdvertisementSelector = ({
  selectedRowIds = [],
  onSelectionChange,
  defaultSelectedColumns = [
    "state",
    "created_datetime",
    "ad_sales",
    "ad_spend",
    "acos",
    "roas",
    "orders",
    "impressions",
    "clicks",
    "ctr",
    "conversion_rate",
    "cpc",
  ],
  enableMultiRowSelection = true,
  canSetPageSize = true,
  context,
}: {
  selectedRowIds?: string[] | number[];
  onSelectionChange: (selected: number[] | string[], selectedRows: AdvertisementQueryV4Row[]) => void;
  defaultSelectedColumns?: string[];
  enableMultiRowSelection?: boolean;
  canSetPageSize?: boolean;
  context: string;
}) => {
  const { t } = useTranslation(["advertising"]);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const columns = useAdReportColumns();

  const EXCLUDED_COLUMNS = ["state", "daily_budget", "target_acos", "target_roas", "max_acos", "min_roas"];

  const filteredColumns = columns
    .filter((c) => !c.id || (c.id && !EXCLUDED_COLUMNS.includes(c.id)))
    .map((c) => {
      if (c.header === "Advertisement") {
        return { ...c, size: 500 };
      }
      return c;
    });

  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id!),
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `adSelectorWidget-${context}`,
    initialState: {
      columnVisibility: defaultHiddenCols,
      pagination: {
        pageSize: 5,
        pageIndex: 0,
      },
      columnOrder: defaultSelectedColumns,
      columnPinning: {
        start: ["ad_name"],
        end: [],
      },
      sorting: [{ id: "ad_sales", desc: true }],
      rowSelection: selectedIdsToSelectionState(selectedRowIds),
    },
  });

  const debounceSearch = useDebounce(state.globalFilter, 500);

  const byAdOptionsGetter = useGetV4AdvReportByAd();
  const advByAdReportQuery = useBlockWindowQuery(byAdOptionsGetter, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: state.columnFilters,
    search: debounceSearch,
  });

  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");
  const advReportOptionsQuery = useQuery({ ...useGetV4AdvReportOptions(), enabled: !tagsEnhancedEnabled });
  const tagEnhancedOptions = useTagFilterOptions("ADVERTISEMENT", { enabled: tagsEnhancedEnabled });
  const categoriesQuery = useCategoriesQuery();
  const biddingProgramOptionsQuery = useBiddingPrograms();

  const tagOptions: { value: string; label: string }[] = useMemo((): { value: string; label: string }[] => {
    const tags = advReportOptionsQuery.data?.data.tags ?? [];

    return [
      { value: "all", label: t(($) => $.advertising["All Tags"]) },
      ...tags.map((tag: string): { value: string; label: string } => ({
        value: tag,
        label: tag,
      })),
    ];
  }, [t, advReportOptionsQuery.data?.data.tags]);

  const biddingProgramOptions: { value: number | string; label: string }[] = useMemo((): {
    value: number | string;
    label: string;
  }[] => {
    const biddingPrograms =
      biddingProgramOptionsQuery.data?.results.map((program) => ({ value: program.id, label: program.name })) ?? [];

    return [{ value: "all", label: t(($) => $.advertising["All Rules"]) }, ...biddingPrograms];
  }, [t, biddingProgramOptionsQuery.data?.results]);

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: t(($) => $.advertising["All Categories"]) },
      ...(categoriesQuery.data?.map((category) => {
        return {
          value: category.id.toString(),
          label: category.leaf_category_name,
        };
      }) ?? []),
    ],
    [t, categoriesQuery.data],
  );
  const table = useServerTable({
    data: advByAdReportQuery.data?.data,
    columns: filteredColumns,
    getRowCanExpand: () => true,
    getRowId: (row) => row.ad_id.toString(),
    enableRowSelection: (row) => {
      return (
        !(row.original.ad_goal === GOALS.IMPORTED_ADS.value && channel === "walmart") && !row.original.multi_marketplace
      );
    },
    enableMultiRowSelection: enableMultiRowSelection,
    rowCount: advByAdReportQuery.data?.count || 0,
    initialState: initialState,
    state: {
      ...state,
      rowSelection: selectedIdsToSelectionState(selectedRowIds),
    },
    ...setters,
    alternateSortingEnabled: true,
    onRowSelectionChange: (updater) => {
      const rowSelectionState = typeof updater === "function" ? updater(table.state.rowSelection) : updater;
      const updatedIds = Object.keys(rowSelectionState);
      const selectedRows = table
        .getRowModel()
        .rows.filter((row) => rowSelectionState[row.id])
        .map((row) => row.original);
      onSelectionChange(updatedIds, selectedRows);
    },
    meta: {
      showSkeleton: advByAdReportQuery.isFetching && (!advByAdReportQuery.data || advByAdReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        tags: tagOptions,
        "tags_enhanced.id": tagEnhancedOptions,
        category_id: categoryOptions,
        state: [
          { value: "all", label: t(($) => $.advertising["All States"]) },
          AD_STATES.ACTIVE,
          AD_STATES.PAUSE,
          AD_STATES.ENDED,
        ],
        ad_status: [{ value: "all", label: t(($) => $.advertising["All Statuses"]) }, ...Object.values(AD_STATUSES)],
        serving_status: servingStatusOptions,
        day_parting_method: [
          {
            value: "all",
            label: t(($) => $.advertising["All"]),
          },
          {
            value: true,
            label: t(($) => $.advertising["With Dayparting"]),
          },
          {
            value: false,
            label: t(($) => $.advertising["Without Dayparting"]),
          },
        ],
        ntm_enrollment_status: [
          {
            label: t(($) => $.advertising["All Statuses"]),
            value: "all",
          },
          ...NTM_STATUS_OPTIONS,
        ],
        has_active_scheduled_actions: [
          {
            value: true,
            label: t(($) => $.advertising["With Scheduled Actions"]),
          },
          {
            value: false,
            label: t(($) => $.advertising["Without Scheduled Actions"]),
          },
        ],
        program_id: biddingProgramOptions,
        ad_source: [
          {
            label: t(($) => $.advertising["All Sources"]),
            value: "all",
          },
          {
            value: "Trellis",
            label: "Trellis",
          },
          {
            value: "Imported",
            label: t(($) => $.advertising["Imported"]),
          },
        ],
      },
    },
  });

  return (
    <div>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack>
          <HStack>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.advertising["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination table={table} canSetPageSize={canSetPageSize} />
      </HStack>
      <Table table={table} />
    </div>
  );
};

export default AdvertisementSelector;
